/* eslint-disable react/prop-types */
import React, { useState, useEffect, useCallback } from 'react';
import { Box, Typography, IconButton, Select, MenuItem, TextField, Button, Dialog, FormControl, FormControlLabel, RadioGroup, Radio, CircularProgress, InputLabel, FormLabel} from "@mui/material";
import { Edit, Check, Cancel } from "@mui/icons-material";
import { useFirebase } from "contexts/Firebase";
import { useAuth } from "contexts/User";
import { doc, updateDoc, getDoc, getDocs, collection } from "firebase/firestore";
import moment from "moment";
import { useLazyQuery } from "@apollo/client";
import { Queries } from "services/apollo";
import HouseholdMembers from "./HouseholdMembers";
import { useRemoteConfig } from "contexts/RemoteConfig";
// import { SelectChangeEvent } from '@mui/material/Select';
import PlanSelectionDialog from './PlanSelectionDialog';
import PlanTypePref from "./PlanTypePreferences";
import CoveragePref from "./CoveragePreferences";

interface PremiumTier {
    employeeContribution: number;
    employerContribution: number;
    totalPremium: number;
}

interface FourTierPremium {
    employeeOnly: PremiumTier;
    employeePlusChildren: PremiumTier;
    employeePlusSpouse: PremiumTier;
    family: PremiumTier;
}

interface BenefitPlan {
    id: string;
    name: string;
    carrier: string;
    planNetworkType: string;
    individualDeductible: number;
    familyDeductible: number;
    individualOutOfPocketMaximum: number;
    familyOutOfPocketMaximum: number;
    fourTierPremium: FourTierPremium;
}

interface EnrollmentInformationProps {
    sendPrompt: (message: string) => void;
    setCurrentSettings: React.Dispatch<React.SetStateAction<{
        insuranceStatus: boolean | null;
        effectiveDate: string | null;
        selectedPlan: any;
        whenBenefits: any[];
        benefitPlan: string | null;
    }>>;
}

const EnrollmentInformation: React.FC<EnrollmentInformationProps> = React.memo(({ 
    sendPrompt,
    setCurrentSettings 
}) => {
    const { firestore } = useFirebase();
    const { user, answers, userData } = useAuth();
    const [conversationId] = useState<string | null>(userData?.get("conversationId"));
    const ideon = useRemoteConfig("ideon").asBoolean();
    const [members, setMembers] = useState<{ [key: string]: any }>({});
    const [editHouseholdOpen, setEditHouseholdOpen] = useState(false);
    const [editZipcode, setEditZipcode] = useState(false);
    const [editCounty, setEditCounty] = useState(false);
    const [zipcode, setZipcode] = useState("");
    const [county, setCounty] = useState("");
    const [editEffectiveDate, setEditEffectiveDate] = useState(false);
    const [effectiveDate, setEffectiveDate] = useState("");
    const [GET_GEOGRAPHY] = useLazyQuery(Queries.GET_GEOGRAPHY);
    const [counties, setCounties] = useState<{ text: string, value: { fips: string, name: string, state: string, zipcode: string } }[]>([]);
    const [loadRates, rateQuery] = useLazyQuery<{ total: number }, { effectiveDate: string, source?: string }>(Queries.getRates, {
        notifyOnNetworkStatusChange: true,
    });
    const [promptSent, setPromptSent] = useState(true);
    const [native, setNative] = useState(false);
    const [checkPremiumTaxCredits, setCheckPremiumTaxCredits] = useState(true);
    const [, setErrors] = useState<{ native?: string, premiumTaxCredits?: string }>({});
    const [oldInsuranceData, setOldInsuranceData] = useState<any>(null);

    const [householdIncome, setHouseholdIncome] = useState<number>(0);
    const [taxFilingStatus, setTaxFilingStatus] = useState<string>("");
    const [benefitPlans, setBenefitPlans] = useState<BenefitPlan[]>([]);
    const [selectedPlan, setSelectedPlan] = useState<string>("");
    const [showPlanSelection, setShowPlanSelection] = useState(false);
    const [planConfirmed, setPlanConfirmed] = useState(false);
    const [showPlanTypePreferences, setShowPlanTypePreferences] = useState(false);
    const [showCoveragePreferences, setShowCoveragePreferences] = useState(false);
    const [hasUnsavedPlanTypePreferences, setHasUnsavedPlanTypePreferences] = useState(false);
    const [hasUnsavedCoveragePreferences, setHasUnsavedCoveragePreferences] = useState(false);

    // Memoized functions
        const saveInsuranceData = useCallback(async (oldData: any, newData: any) => {
        if (!user?.uid || !conversationId) {
            console.error("User is not authenticated or conversation ID is missing");
            return;
        }

        try {
            const convoRef = doc(firestore, `users/${user.uid}/convos/${conversationId}`);
            const convoSnapshot = await getDoc(convoRef);

            if (convoSnapshot.exists()) {
                const convoData = convoSnapshot.data();
                const messages = convoData.messages || [];

                if (messages.length > 0) {
                    const recentMessage = messages[messages.length - 1];
                    recentMessage.data = {
                        ...recentMessage.data,
                        oldInsuranceDetails: {
                            ...oldData,
                            householdIncome: oldData.householdIncome || householdIncome,
                            taxFilingStatus: oldData.taxFilingStatus || taxFilingStatus
                        },
                        newInsuranceDetails: {
                            ...newData,
                            householdIncome: newData.householdIncome || householdIncome,
                            taxFilingStatus: newData.taxFilingStatus || taxFilingStatus
                        }
                    };
                    await updateDoc(convoRef, { messages: messages });
                }
            }
        } catch (error) {
            console.error("Error saving insurance data:", error);
        }
    }, [user?.uid, conversationId, firestore, householdIncome, taxFilingStatus]);


    const updateAnswers = useCallback(async (key: string, value: any) => {
        if (answers && user?.uid) {
            const insuranceDetailsRef = doc(firestore, `users/${user.uid}/answers/${user.uid}`);
            
            const oldDataSnap = await getDoc(insuranceDetailsRef);
            const oldData = oldDataSnap.data()?.insuranceDetails || {};
    
            if (key === "insuranceDetails.householdIncome") {
                await updateDoc(insuranceDetailsRef, {
                    [key]: value,
                    "insuranceDetails.income": value
                });
            } else {
                await updateDoc(insuranceDetailsRef, { [key]: value });
            }
    
            const newDataSnap = await getDoc(insuranceDetailsRef);
            const newData = newDataSnap.data()?.insuranceDetails || {};
    
            await saveInsuranceData(oldData, newData);
        }
    }, [answers, firestore, user, saveInsuranceData]);

    const getCounties = useCallback(async (zipcode: string) => {
        const counties = await GET_GEOGRAPHY({
            query: Queries.GET_GEOGRAPHY,
            variables: { zipcode: zipcode }
        });
        setCounties(counties.data.geographyByZip.map((county: { fips: string, name: string, state: string, zipcode: string }) => ({
            text: county.name,
            value: county
        })));
    }, [GET_GEOGRAPHY]);

    const saveBenefitPlanToConversation = useCallback(async (planId: string) => {
        if (!user?.uid || !conversationId) {
            console.error("User is not authenticated or conversation ID is missing");
            return;
        }
    
        try {
            const selectedPlan = benefitPlans.find(plan => plan.id === planId);
            if (!selectedPlan) {
                console.error("Selected plan not found");
                return;
            }
    
            // Create plan details object to reuse
            const planDetails = {
                id: selectedPlan.id,
                name: selectedPlan.name,
                carrier: selectedPlan.carrier,
                planNetworkType: selectedPlan.planNetworkType,
                individualDeductible: selectedPlan.individualDeductible,
                familyDeductible: selectedPlan.familyDeductible,
                individualOutOfPocketMaximum: selectedPlan.individualOutOfPocketMaximum,
                familyOutOfPocketMaximum: selectedPlan.familyOutOfPocketMaximum,
                fourTierPremium: selectedPlan.fourTierPremium
            };
    
            // Save to conversation
            const convoRef = doc(firestore, `users/${user.uid}/convos/${conversationId}`);
            const convoSnapshot = await getDoc(convoRef);
    
            if (convoSnapshot.exists()) {
                const convoData = convoSnapshot.data();
                const messages = convoData.messages || [];
    
                if (messages.length > 0) {
                    const recentMessage = messages[messages.length - 1];
                    recentMessage.data = {
                        ...recentMessage.data,
                        selectedBenefitPlan: planDetails
                    };
                    await updateDoc(convoRef, { messages });
                }
            }
    
            // Save to chat settings
            const chatSettingsRef = doc(firestore, `users/${user.uid}/settings/chat`);
            await updateDoc(chatSettingsRef, {
                selectedBenefitPlan: planDetails
            });
    
            console.log("Benefit plan details saved to conversation and chat settings");
        } catch (error) {
            console.error("Error saving benefit plan details:", error);
        }
    }, [user?.uid, conversationId, firestore, benefitPlans]);    

    const handlePlanSelect = useCallback((planId: string) => {
        setSelectedPlan(planId);
    }, []);

    const handlePlanConfirmation = useCallback(async () => {
        if (selectedPlan) {
            await updateAnswers("insuranceDetails.selectedPlan", selectedPlan);
            await saveBenefitPlanToConversation(selectedPlan);
            setPlanConfirmed(true);
            setShowPlanSelection(false);
        }
    }, [selectedPlan, updateAnswers, saveBenefitPlanToConversation]);

    const togglePlanTypePreferences = () => {
        setShowPlanTypePreferences(!showPlanTypePreferences);
    };

    const toggleCoveragePreferences = () => {
        setShowCoveragePreferences(!showCoveragePreferences);
    };

    const handlePlanTypePreferencesSaved = useCallback(() => {
        setPromptSent(false);
        setShowPlanTypePreferences(false);
    }, []);

    const handleCoveragePreferencesSaved = useCallback(() => {
        setPromptSent(false);
        setShowCoveragePreferences(false);
    }, []);

    const handlePlanTypePreferencesChanged = useCallback((hasUnsavedChanges: boolean) => {
        setPromptSent(true);
        setHasUnsavedPlanTypePreferences(hasUnsavedChanges);
    }, []);

    const handleCoveragePreferencesChanged = useCallback((hasUnsavedChanges: boolean) => {
        setPromptSent(true);
        setHasUnsavedCoveragePreferences(hasUnsavedChanges);
    }, []);

    const handleContinueClick = useCallback(async () => {
        if (!planConfirmed) {
            setShowPlanSelection(true);
            return;
        }

        if (hasUnsavedPlanTypePreferences || hasUnsavedCoveragePreferences) {
            alert("Please save your preference changes before continuing.");
            return;
        }

        setPromptSent(true);
        
        const insuranceDetailsRef = doc(firestore, `users/${user?.uid}/answers/${user?.uid}`);
        const newInsuranceDataSnap = await getDoc(insuranceDetailsRef);
        const newInsuranceData = newInsuranceDataSnap.data()?.insuranceDetails || {};

        await saveInsuranceData(oldInsuranceData, newInsuranceData);
        setOldInsuranceData(newInsuranceData);

        await loadRates({
            variables: {
                effectiveDate: effectiveDate,
                source: ideon ? "ideon" : "quotit"
            },
            fetchPolicy: "network-only"
        });
        sendPrompt("I have completed the form, please provide the recommended plans");
    }, [planConfirmed, hasUnsavedPlanTypePreferences, hasUnsavedCoveragePreferences, firestore, user?.uid, oldInsuranceData, saveInsuranceData, loadRates, effectiveDate, ideon, sendPrompt]);

    const handleHouseholdSave = useCallback(async () => {
        const insuranceDetailsRef = doc(firestore, `users/${user?.uid}/answers/${user?.uid}`);
        const oldInsuranceDataSnap = await getDoc(insuranceDetailsRef);
        const oldData = oldInsuranceDataSnap.data()?.insuranceDetails || {};
        
        const newInsuranceDataSnap = await getDoc(insuranceDetailsRef);
        const newData = newInsuranceDataSnap.data()?.insuranceDetails || {};
        
        await saveInsuranceData(oldData, newData);
        setPromptSent(false);
        setEditHouseholdOpen(false);
    }, [user?.uid, firestore, saveInsuranceData]);

    // Effects
    useEffect(() => {
        const fetchInitialInsuranceData = async () => {
            if (user?.uid) {
                const insuranceDetailsRef = doc(firestore, `users/${user.uid}/answers/${user.uid}`);
                const insuranceDataSnap = await getDoc(insuranceDetailsRef);
                const initialInsuranceData = insuranceDataSnap.data()?.insuranceDetails || {};
                setOldInsuranceData(initialInsuranceData);
                if (initialInsuranceData.income) {
                    setHouseholdIncome(initialInsuranceData.income);
                }
                if (initialInsuranceData.taxFilingStatus) {
                    setTaxFilingStatus(initialInsuranceData.taxFilingStatus);
                }
            }
        };
        fetchInitialInsuranceData();
    }, [user, firestore]);

    useEffect(() => {
        const fetchBenefitPlans = async () => {
            try {
                const benefitPlansRef = collection(firestore, 'companies/when/benefitPlans');
                const benefitPlansSnapshot = await getDocs(benefitPlansRef);
                const plans = benefitPlansSnapshot.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data(),
                } as BenefitPlan));
                setBenefitPlans(plans);
            } catch (error) {
                console.error("Error fetching benefit plans:", error);
            }
        };

        fetchBenefitPlans();
    }, [firestore]);

    useEffect(() => {
        const mems = answers?.get('insuranceDetails.household.members');
        if (mems === undefined) {
            setMembers({});
        } else {
            setMembers(mems);
        }
        if (answers?.get("insuranceDetails.zipcode")) {
            setZipcode(answers?.get("insuranceDetails.zipcode"));
        }
        
        const defaultEffectiveDate = moment().add(1, 'months').startOf('month').format("YYYY-MM-DD");
        if (!answers?.get("insuranceDetails.effectiveDate")) {
            setEffectiveDate(defaultEffectiveDate);
            updateAnswers("insuranceDetails.effectiveDate", defaultEffectiveDate);
        } else {
            setEffectiveDate(answers.get("insuranceDetails.effectiveDate"));
        }

        if (answers?.get("insuranceDetails.native")) {
            setNative(answers?.get("insuranceDetails.native"));
        }
        if (answers?.get("insuranceDetails.checkPremiumTaxCredits") !== undefined) {
            setCheckPremiumTaxCredits(answers.get("insuranceDetails.checkPremiumTaxCredits"));
        } else {
            setCheckPremiumTaxCredits(true);
            updateAnswers("insuranceDetails.checkPremiumTaxCredits", true);
        }
    }, [answers, updateAnswers]);

    // Memoized values
    const currentHousehold = React.useMemo(() => 
        answers?.get('insuranceDetails.household.members'),
        [answers]
    );

    return (
        <Box sx={{
            display: "flex",
            flexDirection: "column",
            gap: "24px",
            backgroundColor: "#f9f9f9",
            p: 3,
            borderRadius: "12px",
            width: "100%",
            maxWidth: "650px",
            margin: "0 auto",
        }}>
            <Typography 
                variant="h6" 
                sx={{ 
                    color: '#333',
                    fontSize: '22px',
                    fontWeight: 600,
                    mb: 1
                }}
            >
                Your enrollment details
            </Typography>

            {/* Effective Date Section */}
            <Box sx={{
                display: "flex",
                flexDirection: "column",
                gap: "24px",
                backgroundColor: "#fff",
                p: 3,
                borderRadius: "8px",
            }}>
                <Box sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    width: "100%",
                    borderBottom: '1px solid #e0e0e0',
                    pb: 3
                }}>
                    {!editEffectiveDate ? (
                        <>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <Typography sx={{ color: '#1f1f1f', fontSize: '16px' }}>
                                    Effective date:
                                </Typography>
                                <Typography sx={{ ml: 1, color: '#1f1f1f', fontSize: '16px', fontWeight: 800 }}>
                                    {moment(effectiveDate).format("MMMM DD, YYYY")}
                                </Typography>
                            </Box>
                            <IconButton 
                                size="small" 
                                onClick={() => setEditEffectiveDate(true)}
                                sx={{ color: '#f5923e' }}
                            >
                                <Edit fontSize="small" />
                            </IconButton>
                        </>
                    ) : (
                        <Box sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 2,
                            width: "100%"
                        }}>
                            <Typography>Effective date: </Typography>
                            <Select 
                                size="small"
                                value={effectiveDate} 
                                onChange={(e) => setEffectiveDate(e.target.value)}
                                sx={{ flexGrow: 1 }}
                            >
                                {Array.from(Array(4).keys()).map((i) => {
                                    const date = moment().add(i, "months").startOf("month");
                                    return (
                                        <MenuItem key={date.format("YYYY-MM-DD")} value={date.format("YYYY-MM-DD")}>
                                            {date.format("MMMM D, YYYY")}
                                        </MenuItem>
                                    )
                                })}
                            </Select>
                            <Box>
                                <IconButton size="small" onClick={() => { 
                                    setPromptSent(false); 
                                    updateAnswers("insuranceDetails.effectiveDate", effectiveDate); 
                                    setEditEffectiveDate(false) 
                                }}>
                                    <Check fontSize="small" />
                                </IconButton>
                                <IconButton size="small" onClick={() => setEditEffectiveDate(false)}>
                                    <Cancel fontSize="small" />
                                </IconButton>
                            </Box>
                        </Box>
                    )}
                </Box>

                {/* Zipcode Section */}
                <Box sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    width: "100%",
                    borderBottom: '1px solid #e0e0e0',
                    pb: 3
                }}>
                    {!editZipcode ? (
                        <>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <Typography sx={{ color: '#1f1f1f', fontSize: '16px' }}>
                                    Zipcode:
                                </Typography>
                                <Typography sx={{ ml: 1, color: '#1f1f1f', fontSize: '16px', fontWeight: 800 }}>
                                    {answers?.get("insuranceDetails.zipcode")}
                                </Typography>
                            </Box>
                            <IconButton 
                                size="small" 
                                onClick={() => setEditZipcode(true)}
                                sx={{ color: '#f5923e' }}
                            >
                                <Edit fontSize="small" />
                            </IconButton>
                        </>
                    ) : (
                        <Box sx={{
                            display: "flex",
                            flexDirection: "row",
                            alignItems: "center",
                            gap: "10px",
                        }}>
                            {!editCounty && (
                                <>
                                    <Typography>Zipcode: </Typography>
                                    <TextField 
                                        size="small"
                                        value={zipcode} 
                                        onChange={(e) => { setZipcode(e.target.value) }}
                                    />
                                    <IconButton size="small" onClick={() => { getCounties(zipcode); setEditCounty(true) }}><Check fontSize="small" /></IconButton>
                                </>
                            )}
                            {editCounty && (
                                <>
                                    <Typography>County: </Typography>
                                    <Select 
                                        size="small"
                                        value={county} 
                                        onChange={(e) => { setCounty(e.target.value) }}
                                    >
                                        {counties.map((county) => (
                                            <MenuItem key={county.text}
                                                value={county.text}>
                                                {county.text}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                    <IconButton size="small" onClick={() => {
                                        updateAnswers("insuranceDetails.zipcode", zipcode);
                                        updateAnswers("insuranceDetails.county", counties.find(p => p.text === county)?.value);
                                        setPromptSent(false);
                                        setEditZipcode(false);
                                        setEditCounty(false);
                                    }}><Check fontSize="small" /></IconButton>
                                </>
                            )}
                            <IconButton size="small" onClick={() => { setEditZipcode(false); setEditCounty(false); }}><Cancel fontSize="small" /></IconButton>
                        </Box>
                    )}
                </Box>

                {/* Household Members Section */}
                <Box sx={{ 
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'stretch',
                    position: 'relative'
                }}>
                    <Box sx={{
                        backgroundColor: '#f0f0f0',
                        p: 2,
                        borderRadius: '8px'
                    }}>
                        {Object.entries(members).map(([key, member]) => (
                            <Box 
                                key={key} 
                                sx={{ 
                                    mb: 2,
                                    p: 2,
                                    backgroundColor: '#fff',
                                    border: '1px solid #e0e0e0',
                                    borderRadius: '8px',
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'flex-start'
                                }}
                            >
                                <Box>
                                    <Typography sx={{ 
                                        fontSize: '16px',
                                        fontWeight: 500,
                                        color: '#1f1f1f',
                                        mb: 0.5
                                    }}>
                                        {member.name}
                                    </Typography>
                                    <Typography sx={{ 
                                        fontSize: '14px',
                                        color: '#666',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '4px'
                                    }}>
                                        {member.relationship.charAt(0).toUpperCase() + member.relationship.slice(1)} • {member.gender.charAt(0).toUpperCase() + member.gender.slice(1)} • Born on {moment(member.dob).format('MMMM D, YYYY')}
                                        {member.tobaccoUser && ' • Tobacco User'}
                                        {member.pregnant && ' • Pregnant'}
                                    </Typography>
                                </Box>
                                <IconButton 
                                    size="small"
                                    sx={{ color: '#f5923e' }}
                                    onClick={() => setEditHouseholdOpen(true)}
                                >
                                    <Edit fontSize="small" />
                                </IconButton>
                            </Box>
                        ))}
                        <Button 
                            variant="outlined" 
                            onClick={() => setEditHouseholdOpen(true)}
                            sx={{
                                width: '40%',
                                color: '#333',
                                borderColor: '#333',
                                textTransform: 'none',
                                backgroundColor: '#FFFFFF',
                                fontSize: '16px',
                                '&:hover': {
                                    borderColor: '#333',
                                    backgroundColor: 'rgba(0, 0, 0, 0.05)'
                                }
                            }}
                        >
                            Update Household
                        </Button>
                    </Box>
                </Box>
            </Box>

            {/* Plan Preferences Section */}
            <Box sx={{ width: '100%' }}>
                <Box sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 3
                }}>
                    <Typography sx={{ color: '#1f1f1f', fontSize: '16px' }}>
                        What type of plan do you prefer?
                    </Typography>
                    <Button 
                        variant="outlined"
                        size="small"
                        onClick={togglePlanTypePreferences}
                        sx={{
                            color: '#1f1f1f',
                            borderColor: '#1f1f1f',
                            textTransform: 'none',
                            fontSize: '14px',
                            padding: '4px 16px',
                            '&:hover': {
                                borderColor: '#1f1f1f',
                                backgroundColor: 'rgba(0, 0, 0, 0.04)'
                            }
                        }}
                    >
                        Update Preferences
                    </Button>
                </Box>
                
                <Box sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 3
                }}>
                    <Typography sx={{ color: '#1f1f1f', fontSize: '16px' }}>
                        What plan features are important to you?
                    </Typography>
                    <Button 
                        variant="outlined"
                        size="small"
                        onClick={toggleCoveragePreferences}
                        sx={{
                            color: '#1f1f1f',
                            borderColor: '#1f1f1f',
                            textTransform: 'none',
                            fontSize: '14px',
                            padding: '4px 16px',
                            '&:hover': {
                                borderColor: '#1f1f1f',
                                backgroundColor: 'rgba(0, 0, 0, 0.04)'
                            }
                        }}
                    >
                        Update Preferences
                    </Button>
                </Box>
            </Box>

            {/* Premium Tax Credits Section */}
            <Box sx={{ width: '100%' }}>
                <FormControl component="fieldset" sx={{ width: '100%' }}>
                    <Box sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        mb: 2
                    }}>
                        <Typography sx={{ color: '#1f1f1f', fontSize: '16px' }}>
                            Do you want to check for premium tax credits?
                        </Typography>
                        <RadioGroup
                            row
                            name="premiumTaxCredits"
                            value={checkPremiumTaxCredits ? "true" : "false"}
                            onChange={(e) => {
                                const newValue = e.target.value === 'true';
                                setCheckPremiumTaxCredits(newValue);
                                setPromptSent(false);
                                updateAnswers("insuranceDetails.checkPremiumTaxCredits", newValue);
                            }}
                        >
                            <FormControlLabel 
                                value="true" 
                                control={
                                    <Radio 
                                        size="small"
                                        sx={{
                                            '&.Mui-checked': {
                                                color: '#1f1f1f',
                                            }
                                        }}
                                    />
                                } 
                                label={<Typography sx={{ fontSize: '14px' }}>Yes</Typography>}
                            />
                            <FormControlLabel 
                                value="false" 
                                control={
                                    <Radio 
                                        size="small"
                                        sx={{
                                            '&.Mui-checked': {
                                                color: '#1f1f1f',
                                            }
                                        }}
                                    />
                                } 
                                label={<Typography sx={{ fontSize: '14px' }}>No</Typography>}
                            />
                        </RadioGroup>
                    </Box>
                </FormControl>

                {checkPremiumTaxCredits && (
                    <>
                        <Box sx={{ 
                            display: 'flex', 
                            flexDirection: 'column', 
                            gap: 1,
                            mt: 1
                        }}>
                            <TextField
                                size="small"
                                label="Household Income"
                                value={householdIncome}
                                sx={{pb:2}}
                                onChange={(e) => {
                                    const newIncome = parseInt(e.target.value, 10) || 0;
                                    setHouseholdIncome(newIncome);
                                    updateAnswers("insuranceDetails.householdIncome", newIncome);
                                    setPromptSent(false);
                                }}
                            />
                            <FormControl size="small">
                                <InputLabel id="tax-filing-status-label">Tax Filing Status</InputLabel>
                                <Select
                                    labelId="tax-filing-status-label"
                                    label="Tax Filing Status"
                                    value={taxFilingStatus}
                                    onChange={(e) => {
                                        const newStatus = e.target.value as string;
                                        setTaxFilingStatus(newStatus);
                                        updateAnswers("insuranceDetails.taxFilingStatus", newStatus);
                                        setPromptSent(false);
                                    }}
                                >
                                    <MenuItem value="Single">Single</MenuItem>
                                    <MenuItem value="HeadOfHousehold">Head of Household</MenuItem>
                                    <MenuItem value="QualifyingWidower">Qualifying Widower</MenuItem>
                                    <MenuItem value="MarriedFilingJointly">Married filing jointly</MenuItem>
                                    <MenuItem value="MarriedFilingSeparately">Married filing separately</MenuItem>
                                </Select>
                            </FormControl>
                        </Box>

                        {/* Native American Check */}
                        <FormControl component="fieldset" sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, mt: 2 }}>
                            <FormLabel sx={{ fontWeight: 'bold', top: '10px' }}>Are you a Native American or Alaskan Native? &nbsp;</FormLabel>
                            <RadioGroup
                                sx={{ flexDirection: { xs: 'column', sm: 'row' }, marginLeft: { sm: 'auto' } }}
                                row
                                name="native"
                                value={native ? "true" : "false"}
                                onChange={(e) => {
                                    const newValue = e.target.value === 'true';
                                    setNative(newValue);
                                    setPromptSent(false);
                                    updateAnswers("insuranceDetails.native", newValue);
                                    if (newValue) {
                                        setErrors(prevErrors => ({ ...prevErrors, native: '' }));
                                    }
                                }}
                            >
                                <FormControlLabel value="true" control={<Radio />} label="Yes" />
                                <FormControlLabel value="false" control={<Radio />} label="No" />
                            </RadioGroup>
                        </FormControl>
                    </>
                )}
            </Box>

            {/* Continue Button */}
            <Button
                variant="contained"
                disabled={promptSent || hasUnsavedPlanTypePreferences || hasUnsavedCoveragePreferences}
                onClick={handleContinueClick}
                sx={{
                    width: '100%',
                    mt: 2,
                    backgroundColor: '#333',
                    color: '#fff',
                    textTransform: 'none',
                    fontSize: '16px',
                    boxShadow: 'none',
                    '&:hover': {
                        backgroundColor: '#444',
                        boxShadow: 'none'
                    },
                    '&:disabled': {
                        backgroundColor: '#e0e0e0',
                        color: '#666'
                    }
                }}
            >
                Continue
            </Button>

            {/* Existing dialogs and loading states */}
            <Dialog
                open={editHouseholdOpen}
                onClose={() => setEditHouseholdOpen(false)}
                fullWidth
                maxWidth="lg"
            >
                <Box sx={{ p: 4, width: "100%" }}>
                    <HouseholdMembers 
                        saved={handleHouseholdSave} 
                        cancel={() => setEditHouseholdOpen(false)} 
                    />                        
                </Box>
            </Dialog>

            <Dialog
                open={showPlanTypePreferences}
                onClose={() => setShowPlanTypePreferences(false)}
                fullWidth
                maxWidth="md"
            >
                <Box sx={{ p: 4, width: "100%" }}>
                    <PlanTypePref
                        onPreferencesSaved={handlePlanTypePreferencesSaved}
                        onPreferencesChanged={handlePlanTypePreferencesChanged}
                    />
                </Box>
            </Dialog>

            <Dialog
                open={showCoveragePreferences}
                onClose={() => setShowCoveragePreferences(false)}
                fullWidth
                maxWidth="md"
            >
                <Box sx={{ p: 4, width: "100%" }}>
                    <CoveragePref
                        onPreferencesSaved={handleCoveragePreferencesSaved}
                        onPreferencesChanged={handleCoveragePreferencesChanged}
                    />
                </Box>
            </Dialog>

            {rateQuery.loading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                    <CircularProgress sx={{ color: '#1f1f1f' }} />
                </Box>
            )}

            <PlanSelectionDialog
                open={showPlanSelection}
                onClose={() => setShowPlanSelection(false)}
                selectedPlan={selectedPlan}
                onPlanSelect={handlePlanSelect}
                onConfirm={handlePlanConfirmation}
                benefitPlans={benefitPlans}
                household={currentHousehold}
                setCurrentSettings={setCurrentSettings}
            />
        </Box>
    );
});

EnrollmentInformation.displayName = 'EnrollmentInformation';

export default EnrollmentInformation;